/**
 * Statistical process control — an individuals & moving-range (I-MR) chart
 * with the four Western Electric run rules and Cpk against the metric's limit.
 *
 * ## The chart
 *
 * - **Phase-I baseline.** Limits are computed from the first `baselineN`
 *   points (default 20 — the usual minimum for I-MR limits) and then applied
 *   to the whole series, so a process that drifts after the baseline shows up
 *   as signals rather than being absorbed into wider limits. Pass
 *   `baselineN: Infinity` for limits over the whole series.
 * - `mean` = baseline mean; `MR̄` = mean moving range over the baseline;
 *   `σ = MR̄ / 1.128` (d₂ for n = 2); `UCL/LCL = mean ± 3σ`;
 *   `MR UCL = 3.267 × MR̄` (D₄ for n = 2).
 * - **Fewer than 20 points** → `state: 'insufficient'`, no signals. The
 *   11-point daily metrics are therefore always insufficient on their own;
 *   the 240-sample live replays are not.
 * - **Cpk** against `usl`/`lsl` when the series has one:
 *   `min(USL − mean, mean − LSL) / 3σ`. Negative when the mean sits past the
 *   limit.
 *
 * ## Run rules (Western Electric, as Nelson numbers them)
 *
 * 1. One point beyond 3σ.
 * 2. **Nine** points in a row on the same side of the mean. The original
 *    Western Electric handbook uses eight; nine is the Nelson / AIAG SPC
 *    manual convention and halves the false-alarm rate on a single chart.
 *    Configurable via `runLength`.
 * 3. Six points in a row steadily increasing or decreasing.
 * 4. Two of three consecutive points beyond 2σ on the same side.
 *
 * Every index that completes a pattern is reported, so a long run reports
 * each point past the ninth.
 *
 * ## OptiFlow swap
 *
 * Miles's SPC module for OptiFlow is the intended long-term engine. Everything
 * here is behind the {@link SpcEngine} interface: a scene asks for
 * `spcEngine.imr(series)` and renders an `SpcResult`. Swapping in OptiFlow is
 * a new object implementing `SpcEngine` (an adapter over its API) assigned
 * where {@link spcEngine} is exported — no scene changes. The series shape
 * (`SpcSeries`) is deliberately plain — timestamps, values, spec limits — so
 * it maps onto any SPC service's input.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions. No
 * React, no clock, no randomness.
 */

import type { SpcPoint, SpcResult, SpcSeries, SpcSignal } from '../../schema/operations.ts';
import type {
  DailyMetricRow,
  LiveReplayRegistry,
  LiveSample,
  LiveTruckSample,
} from '../../schema/telemetry.ts';
import { metricInfoFor } from '../seed/metric-info.ts';
import { round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';

/** d₂ for subgroups of 2 — σ̂ = MR̄ / d₂. */
export const D2 = 1.128;
/** D₄ for subgroups of 2 — MR UCL = D₄ × MR̄. */
export const D4 = 3.267;
/** Fewer points than this and the chart is `'insufficient'`. */
export const MIN_POINTS = 20;

/** Chart options. */
export interface SpcOptions {
  /** Points the limits are computed from. Default {@link MIN_POINTS}. */
  readonly baselineN?: number;
  /** Rule 2 run length. Default 9. */
  readonly runLength?: number;
}

/**
 * The seam for Miles's OptiFlow SPC module. Anything that turns an
 * `SpcSeries` into an `SpcResult` can stand in for the local engine.
 */
export interface SpcEngine {
  /** Stable id, e.g. `'local-imr'` or `'optiflow'`. */
  readonly id: string;
  /** Shown under the chart, e.g. `'AiRis I-MR (local)'`. */
  readonly label: string;
  imr(series: SpcSeries, options?: SpcOptions): SpcResult;
}

/** Cpk for a mean and σ against whichever spec limits exist; `undefined` with none. */
export function cpk(mean: number, sigma: number, usl?: number, lsl?: number): number | undefined {
  if (sigma <= 0 || (usl === undefined && lsl === undefined)) return undefined;
  const up = usl === undefined ? Infinity : (usl - mean) / (3 * sigma);
  const lo = lsl === undefined ? Infinity : (mean - lsl) / (3 * sigma);
  return Math.min(up, lo);
}

/** The four run rules over `values`, given the chart centre and σ. */
export function runRules(values: readonly number[], mean: number, sigma: number, runLength = 9): SpcSignal[] {
  const out: SpcSignal[] = [];
  if (sigma <= 0) return out;
  const side = (v: number) => (v > mean ? 1 : v < mean ? -1 : 0);
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (Math.abs(v - mean) > 3 * sigma) {
      out.push({ rule: 1, index: i, description: `Point ${v} beyond 3σ (${v > mean ? 'above UCL' : 'below LCL'})` });
    }
    if (i >= runLength - 1) {
      const w = values.slice(i - runLength + 1, i + 1).map(side);
      if (w.every((s) => s === 1) || w.every((s) => s === -1)) {
        out.push({ rule: 2, index: i, description: `${runLength} points in a row ${w[0] === 1 ? 'above' : 'below'} the mean` });
      }
    }
    if (i >= 5) {
      const w = values.slice(i - 5, i + 1);
      let up = true;
      let down = true;
      for (let j = 1; j < w.length; j += 1) {
        if (!(w[j] > w[j - 1])) up = false;
        if (!(w[j] < w[j - 1])) down = false;
      }
      if (up || down) out.push({ rule: 3, index: i, description: `6 points in a row steadily ${up ? 'rising' : 'falling'}` });
    }
    if (i >= 2) {
      const w = values.slice(i - 2, i + 1);
      const hi = w.filter((x) => x > mean + 2 * sigma).length;
      const lo = w.filter((x) => x < mean - 2 * sigma).length;
      const beyond = v > mean + 2 * sigma || v < mean - 2 * sigma;
      if (beyond && (hi >= 2 || lo >= 2)) {
        out.push({ rule: 4, index: i, description: `2 of 3 points beyond 2σ ${hi >= 2 ? 'above' : 'below'} the mean` });
      }
    }
  }
  return out;
}

/** Build the I-MR chart for a series. */
export function imrChart(series: SpcSeries, options: SpcOptions = {}): SpcResult {
  const values = series.points.map((p) => p.value);
  const points: SpcPoint[] = series.points.map((p, i) => ({
    x: p.x,
    value: p.value,
    mr: i === 0 ? null : round(Math.abs(p.value - series.points[i - 1].value), 3),
  }));
  const baselineN = Math.min(values.length, options.baselineN ?? MIN_POINTS);
  const base = values.slice(0, baselineN);
  const mean = base.length ? base.reduce((a, b) => a + b, 0) / base.length : 0;
  let mrSum = 0;
  for (let i = 1; i < base.length; i += 1) mrSum += Math.abs(base[i] - base[i - 1]);
  const mrBar = base.length > 1 ? mrSum / (base.length - 1) : 0;
  const sigma = mrBar / D2;

  const insufficient = values.length < MIN_POINTS;
  const signals = insufficient ? [] : runRules(values, mean, sigma, options.runLength ?? 9);
  const capability = cpk(mean, sigma, series.usl, series.lsl);

  return {
    metric: series.metric,
    unit: series.unit,
    mean: round(mean, 3),
    sigma: round(sigma, 3),
    ucl: round(mean + 3 * sigma, 3),
    lcl: round(mean - 3 * sigma, 3),
    mrBar: round(mrBar, 3),
    mrUcl: round(D4 * mrBar, 3),
    baselineN,
    ...(series.usl !== undefined ? { usl: series.usl } : {}),
    ...(series.lsl !== undefined ? { lsl: series.lsl } : {}),
    ...(capability !== undefined ? { cpk: round(capability, 3) } : {}),
    points,
    signals,
    state: insufficient ? 'insufficient' : signals.length ? 'out-of-control' : 'in-control',
  };
}

/** The local I-MR engine. */
export const localSpcEngine: SpcEngine = {
  id: 'local-imr',
  label: 'AiRis I-MR (local) — swappable for the OptiFlow SPC module',
  imr: imrChart,
};

/**
 * The engine scenes should call. Re-point this at an OptiFlow adapter to swap
 * implementations; nothing else changes.
 */
export const spcEngine: SpcEngine = localSpcEngine;

// ---------------------------------------------------------------------------
// Series builders
// ---------------------------------------------------------------------------

const isTruck = (s: LiveSample): s is LiveTruckSample => 'brake_temp_rr_c' in s;

/** Which live field is charted for a machine, with its unit and spec limit. */
const LIVE_FIELD: Readonly<Record<string, { field: string; unit: string; usl?: number }>> = {
  truck: { field: 'brake_temp_rr_c', unit: '°C', usl: 240 },
  loader: { field: 'intake_air_c', unit: '°C', usl: 48 },
};

/**
 * A machine's live replay as an SPC series — rear-brake temperature on the
 * truck (`T-09`, USL 240 °C, the demo limit), intake-air temperature on the
 * loader (`L-13`, USL 48 °C, the derate threshold alert AL-0915 cites).
 * `null` when the machine has no replay.
 */
export function liveSeries(machine: string, live: LiveReplayRegistry): SpcSeries | null {
  const samples = live[machine];
  if (!samples?.length) return null;
  const spec = isTruck(samples[0]) ? LIVE_FIELD.truck : LIVE_FIELD.loader;
  return {
    machine,
    metric: spec.field,
    unit: spec.unit,
    points: samples.map((s) => ({ x: s.ts, value: (s as unknown as Record<string, number>)[spec.field] })),
    ...(spec.usl !== undefined ? { usl: spec.usl } : {}),
  };
}

/** A machine's 11-day tracked metric as an SPC series, with the `METRIC_INFO` limit as USL. */
export function dailySeries(machine: string, dailyMetric: readonly DailyMetricRow[]): SpcSeries | null {
  const rows = dailyMetric.filter((d) => d.machine_id === machine);
  if (!rows.length) return null;
  const info = metricInfoFor(rows[0].metric);
  return {
    machine,
    metric: rows[0].metric,
    unit: info.unit,
    points: rows.map((r) => ({ x: r.date, value: r.value })),
    ...(info.limit !== null ? { usl: info.limit } : {}),
  };
}

/** Fetch the live replay and chart it. */
export async function loadLiveSpc(machine: string, options: SpcOptions = {}): Promise<SpcResult | null> {
  const { live } = await loadReliabilityInputs();
  const series = liveSeries(machine, live);
  return series ? spcEngine.imr(series, options) : null;
}
